import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { merge } from 'lodash-es';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { CartPaymentCardType } from 'generated/rbi-graphql';
import useReadyQueue from 'hooks/use-ready-queue';
import { legacyFlags } from 'state/datadog-flags/legacy-flags';
import {
  legacyExcludedKeys,
  legacyGatewayKeys,
  legacyKnownKeys,
} from 'state/datadog-flags/legacy-read-metadata';
import { loadRegion } from 'state/intl/load-region';
import { useLogger } from 'state/logger';
import { ServiceMode } from 'state/service-mode';
import { getName } from 'utils/attributes';
import { isProduction, sanityDataset } from 'utils/environment';
import LaunchDarklyHelper, {
  BooleanFlags,
  EnumFlagTypes,
  EnumFlags,
  FlagType,
  LDUser,
  LaunchDarklyFlag,
  LaunchDarklyFlagsObject,
  NumericFlags,
  StringFlags,
  UserAttributeUpdates,
  VariationFlags,
  normalizeUserAttributes,
} from 'utils/launchdarkly';
import noop from 'utils/noop';

import { getFeatureFlagOverridesFromQueryParameters } from './feature-flag-overrides';

export { LaunchDarklyFlag };

// Legacy API names are retained for callers; all evaluations are Datadog-only.

interface ILogin {
  cognitoId?: string;
  details: { name?: string; email: string };
}

export interface ILaunchDarklyCtx {
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser
  ): Promise<FlagType<F> | undefined>;
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultValue: FlagType<F>
  ): Promise<FlagType<F>>;
  flags: LaunchDarklyFlagsObject;
  flagOverrides: { [id: string]: boolean };
  ldUser: LDUser | null;
  /**
   * this allows us to determine whether we are receiving the correct value for
   * user-targeted flags over just the application defaults
   */
  ldUserIsAuthenticated: boolean;
  login: (u: ILogin) => void;
  logout: () => void;
  updateCheckoutSelections: (paymentType?: CartPaymentCardType) => void;
  updateFulfillmentOperations: (operation: string) => void;
  updateUser: (u: UserAttributeUpdates) => void;
  updateUserAttributes: (u: UserAttributeUpdates) => void;
  updateUserLocale: (locale: { region: SupportedRegions; language: SupportedLanguages }) => void;
  updateUserDeviceId: (deviceId: string) => void;
  updateUserStore: (store: IStore | null) => void;
  updateServiceMode: (serviceMode: ServiceMode | null) => void;
}

export const LDContext = React.createContext<ILaunchDarklyCtx>({
  attemptGetUpdatedLdFlag: (_key: any, _attributes: any, defaultValue?: any) =>
    Promise.resolve(defaultValue),
  flags: {},
  flagOverrides: {},
  updateUserDeviceId: noop,
  ldUser: null,
  ldUserIsAuthenticated: false,
  login: noop,
  logout: noop,
  updateCheckoutSelections: noop,
  updateFulfillmentOperations: noop,
  updateServiceMode: noop,
  updateUser: noop,
  updateUserAttributes: noop,
  updateUserLocale: noop,
  updateUserStore: noop,
});

export const useLDContext = () => useContext(LDContext);

export function useFlag(
  flag: LaunchDarklyFlag.TIERED_DELIVERY_FEES | LaunchDarklyFlag.TIERED_CATERING_DELIVERY_FEES
): Record<string, any> | null | undefined;
export function useFlag<T>(flag: VariationFlags): T | null | undefined;
export function useFlag<F extends EnumFlags>(flag: F): EnumFlagTypes<F>;
export function useFlag(flag: NumericFlags): FlagType<NumericFlags>;
export function useFlag(flag: StringFlags): FlagType<StringFlags>;
export function useFlag(flag: BooleanFlags): FlagType<BooleanFlags>;
export function useFlag(flag: LaunchDarklyFlag): FlagType<LaunchDarklyFlag> {
  const { flags, flagOverrides } = useLDContext();

  if (!legacyExcludedKeys.has(flag) && !isProduction && flagOverrides[flag] !== undefined) {
    return flagOverrides[flag];
  }

  // @todo update code to handle missing flags or update function to accept a fallback
  return flags && flags[flag]!;
}

export function useGatewayFlags() {
  const { flags } = useLDContext();

  const gatewayKeys = new Set([
    ...legacyGatewayKeys,
    ...Object.keys(flags).filter(k => k.startsWith('enable-gateway-')),
  ]);
  const gatewayFlags = Array.from(gatewayKeys)
    .map(k => [k, flags[k]] as [string, any])
    .sort((lhs, rhs) => lhs[0].localeCompare(rhs[0]))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  return gatewayFlags;
}

// Preserve the original application dependency after resolving raw DD inputs.
// Provider prerequisites cannot express every application dependency.
// i.e When one flag is on, it can control the value of a different flag
// However, this can not be done for other flag values
// e.g if a flag is off, change the value of a different flag
// This function handles such cases
// Keep this postprocessing separate from source selection.
const checkForDependencies = (flags: LaunchDarklyFlagsObject) => {
  let cloned = { ...flags };
  // Add dependencies here:
  const dependencies = [
    {
      flag: LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0,
      check: () =>
        !flags?.[LaunchDarklyFlag.ENABLE_STATIC_MENU] ||
        flags?.[LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0],
    },
  ];
  dependencies.forEach(({ flag, check }) => {
    cloned = { ...cloned, [flag]: check() };
  });
  return cloned;
};

export function LDProvider(props: { children: ReactNode; defaultFlags: LaunchDarklyFlagsObject }) {
  const [ldUser, setLdUser] = useState<LDUser | null>(null);
  const [flags, setFlags] = useState<LaunchDarklyFlagsObject>(
    checkForDependencies(props.defaultFlags)
  );
  // Preserve the original dependency input shape and render count. These are
  // explicit application/test defaults, never a live provider snapshot.
  const dependencyInputs = useRef(props.defaultFlags);
  const seenKeys = useRef(new Set<string>());
  const ddRevision = useRef(legacyFlags.getRevision());
  useEffect(
    () =>
      legacyFlags.subscribe(() => {
        const revision = legacyFlags.getRevision();
        if (revision !== ddRevision.current) {
          ddRevision.current = revision;
          setFlags(snapshot => ({ ...snapshot }));
        }
      }),
    []
  );
  // Resolve only accessed properties. Enumerating the catalog at boot would
  // evaluate unused flags and would lose dynamic CMS keys absent from LD.
  const effectiveFlags = useMemo(() => {
    const inputs = dependencyInputs.current;
    return new Proxy(flags, {
      ownKeys: snapshot =>
        Array.from(
          new Set([...Reflect.ownKeys(snapshot), ...legacyKnownKeys, ...seenKeys.current])
        ),
      getOwnPropertyDescriptor: (snapshot, key) =>
        Reflect.getOwnPropertyDescriptor(snapshot, key) ||
        (typeof key === 'string' && (legacyKnownKeys.includes(key) || seenKeys.current.has(key))
          ? { configurable: true, enumerable: true, writable: false, value: undefined }
          : undefined),
      get: (snapshot, key) => {
        if (typeof key !== 'string' || key === 'toJSON') {
          return snapshot[key as any];
        }
        seenKeys.current.add(key);
        if (legacyExcludedKeys.has(key)) {
          return snapshot[key];
        }
        if (key === LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0) {
          const staticMenu = legacyFlags.readResult(LaunchDarklyFlag.ENABLE_STATIC_MENU);
          const selection = legacyFlags.readResult(key);
          // Keep the exact legacy dependency result if neither input resolved.
          if (!staticMenu.resolved && !selection.resolved) {
            return snapshot[key];
          }
          return checkForDependencies({
            ...inputs,
            ...(staticMenu.resolved
              ? { [LaunchDarklyFlag.ENABLE_STATIC_MENU]: staticMenu.value }
              : {}),
            ...(selection.resolved ? { [key]: selection.value } : {}),
          })[key];
        }
        return legacyFlags.read(key, snapshot[key]);
      },
    });
  }, [flags]);
  const flagOverrides = useRef(getFeatureFlagOverridesFromQueryParameters());
  const { drainQueue, enqueueIfNotDrained } = useReadyQueue();
  const logger = useLogger();

  // this allows us to determine whether we are receiving the correct value for
  // user-targeted flags over just the application defaults
  const ldUserIsAuthenticated = Boolean(ldUser && !ldUser.anonymous && ldUser.email);

  useEffect(() => {
    // Local identity is available even when optional DD initialization fails.
    drainQueue();
  }, [drainQueue]);

  const updateLDUser = async (changes: UserAttributeUpdates | null) => {
    const helper = LaunchDarklyHelper.getInstance();
    try {
      const updated = changes
        ? await helper.updateCurrentUser(changes)
        : await helper.clearCurrentUser();
      setLdUser(updated.userAttributes);
    } catch {
      logger.error(new Error('Feature flag identity update failed'));
    }
  };

  const updateUser = enqueueIfNotDrained((changes: UserAttributeUpdates) => updateLDUser(changes));

  const updateUserAttributes = enqueueIfNotDrained(
    (user: { key: string; name: string; email: string }): void => {
      return updateUser({
        ...getName({ name: user.name }, { firstName: '', lastName: '' }),
        key: user.key,
        name: user.name,
        email: user.email,
      });
    }
  );

  const updateCheckoutSelections = useCallback(
    enqueueIfNotDrained((paymentType: CartPaymentCardType | undefined): void => {
      return updateUser({
        custom: {
          paymentType: paymentType ?? '',
        },
      });
    }),
    [enqueueIfNotDrained]
  );

  const updateFulfillmentOperations = useCallback(
    enqueueIfNotDrained((operation: string): void => {
      return updateUser({
        custom: {
          operation,
        },
      });
    }),
    [enqueueIfNotDrained]
  );

  const login = enqueueIfNotDrained(({ cognitoId, details: { name = '', email } }: ILogin) => {
    return updateUser({
      ...getName({ name }, { firstName: '', lastName: '' }),
      key: cognitoId,
      name,
      email,
      anonymous: false,
      custom: {
        cognitoId,
        sanityDataset: `${sanityDataset}_${loadRegion().toLowerCase()}`,
      },
    });
  });

  const logout = enqueueIfNotDrained(() => updateLDUser(null));

  const updateUserDeviceId = enqueueIfNotDrained(deviceId => {
    return updateUser({ custom: { device_id: deviceId } });
  });

  const updateUserLocale = enqueueIfNotDrained(
    (locale: { region: SupportedRegions; language: SupportedLanguages }) => {
      return updateUser({
        custom: {
          language: locale.language,
          sanityDataset: `${sanityDataset}_${loadRegion().toLowerCase()}`,
        },
        country: locale.region,
      });
    }
  );

  const updateUserStore = useCallback(
    enqueueIfNotDrained((store: IStore | null) => {
      return updateUser({
        custom: {
          storeNumber: store?.number ?? '',
          storeCity: store?.physicalAddress?.city ?? '',
          storeCountry: store?.physicalAddress?.country ?? '',
          storePostalCode: store?.physicalAddress?.postalCode ?? '',
          storeStateProvince: store?.physicalAddress?.stateProvince ?? '',
          storeStateProvinceShort: store?.physicalAddress?.stateProvinceShort ?? '',
          storeFranchiseGroupName: store?.franchiseGroupName ?? '',
          storePosVendor: store?.pos?.vendor ?? '',
          storePosVersion: store?.pos?.version ?? '',
          storeVatNumber: store?.vatNumber ?? '',
        },
      });
    }),
    [enqueueIfNotDrained]
  );

  const updateServiceMode = useCallback(
    enqueueIfNotDrained((serviceMode: ServiceMode | null) => {
      return updateUser({
        custom: {
          serviceMode: serviceMode ?? '',
        },
      });
    }),
    [enqueueIfNotDrained]
  );

  // Compatibility API: bounded caller-targeted DD evaluation, followed by the
  // caller's original nullish default. No app identity mutation or LD fallback.
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultFlagValue: FlagType<F>
  ): Promise<FlagType<F>>;
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser
  ): Promise<FlagType<F> | undefined>;
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultFlagValue?: FlagType<F>
  ): Promise<FlagType<F> | undefined> {
    const user = merge(
      {},
      LaunchDarklyHelper.getInstance().datadogUserAttributes,
      normalizeUserAttributes(attributes)
    );
    const result = await legacyFlags.readForUser(flagKey, user);
    return result.resolved ? result.value ?? defaultFlagValue : defaultFlagValue;
  }

  return (
    <LDContext.Provider
      value={{
        attemptGetUpdatedLdFlag,
        flags: effectiveFlags,
        flagOverrides: flagOverrides.current,
        ldUser,
        ldUserIsAuthenticated,
        login,
        logout,
        updateCheckoutSelections,
        updateFulfillmentOperations,
        updateServiceMode,
        updateUser,
        updateUserAttributes,
        updateUserLocale,
        updateUserDeviceId,
        updateUserStore,
      }}
    >
      {props.children}
    </LDContext.Provider>
  );
}

export default LDContext.Consumer;
